import {
  Archive,
  Bike,
  CalendarCheck,
  ChartColumn,
  ChefHat,
  CirclePlus,
  ClipboardCheck,
  PackagePlus,
  Clock,
  ConciergeBell,
  Contact,
  Folder,
  History,
  House,
  Layers,
  LayoutGrid,
  MessageSquareHeart,
  Percent,
  ReceiptText,
  RefreshCcw,
  RotateCcw,
  Settings,
  Timer,
  TrendingUp,
  Truck,
  Undo2,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useDeferredValue, useState } from "react";
import { useLocation } from "react-router-dom";
import type { DoodleBadgeId } from "@/shared/doodles/badges.data";
import { endpoints } from "@/lib/api/endpoints";
import type {
  Ingredient,
  IngredientReorderCount,
  MenuItem,
  MenuOnMenuCount,
  Stocktake,
} from "@/lib/api/types";
import { queryKeys, useApiQuery, useObservedQueryData } from "@/shared/queries";
import {
  ingredientsToReorder,
  menuAvailability,
} from "@/modules/manage/lib/today";
import type { ManageNavGroup, ManageNavItem } from "./nav";
import type { ManageRowKey } from "./rows";

/**
 * The PAPER look's nav marks — the redesign's split between big and small.
 *
 * A SECTION heading is a big thing and carries a Doodles badge; a destination
 * under it is a small thing and carries a line icon (Lucide, as the artboard
 * does, at 17px and a 1.75 stroke). The other looks keep `navIcons.tsx`: this
 * map is read only when `paper` is the look, so their sidebars are unchanged.
 *
 * Keyed by row, not by `NavIconKey`: the older set shares one glyph across the
 * five report rows and reuses the van for delivery platforms, and the artboard
 * gives each destination its own.
 */
export const PAPER_SECTION_BADGE: Record<ManageNavGroup, DoodleBadgeId> = {
  service: "servicebell",
  // D152 — the money: the drawer, its log, the تقفيلة, the give-backs.
  money: "register",
  menu: "menu",
  inventory: "warehouse",
  store: "gear",
  reports: "chart",
};

const ROW_ICON: Record<ManageRowKey, LucideIcon> = {
  // The home (D111). The artboard reaches it from the store row at the top
  // of the sidebar; here it is a row of its own, so it carries a mark.
  today: House,
  floor: LayoutGrid,
  invoices: ReceiptText,
  // 2026-10-25 — the customer list, beside the bills.
  customers: Contact,
  shift: Archive,
  // Not a row in the artboard (there the board is a tab on the floor); the
  // app links out to it from here, so it keeps a mark of its own.
  kitchen: ChefHat,
  menu: ConciergeBell,
  addons: CirclePlus,
  categories: Folder,
  ingredients: Layers,
  // D147 — استلام بضاعة.
  receiveDelivery: PackagePlus,
  stocktake: ClipboardCheck,
  suppliers: Truck,
  reportInputVat: Percent,
  aggregators: Bike,
  settings: Settings,
  staff: Users,
  // The spend-gain report is the artboard's "Sales & costs" (2026-10-03);
  // remakes still has no artboard row.
  reports: ChartColumn,
  reportItems: TrendingUp,
  reportStaff: Undo2,
  reportDayparts: Clock,
  reportRemakes: RefreshCcw,
  reportSpeed: Timer,
  reportRefunds: RotateCcw,
  feedback: MessageSquareHeart,
  shiftHistory: History,
  dayClose: CalendarCheck,
};

export function PaperRowIcon({ rowKey }: { rowKey: ManageRowKey }) {
  const Icon = ROW_ICON[rowKey];
  return <Icon size={17} strokeWidth={1.75} aria-hidden="true" />;
}

/**
 * The rows PAPER names as the redesign does (`navPaper.*` in the catalog,
 * founder 2026-10-02). Every other row keeps its `nav.*` label.
 */
export const PAPER_NAV_KEYS = [
  "reports",
  "floor",
  "shift",
  "categories",
  "reportInputVat",
  "reportDayparts",
  "reportStaff",
  "shiftHistory",
  "dayClose",
] as const satisfies readonly ManageRowKey[];
export type PaperNavKey = (typeof PAPER_NAV_KEYS)[number];
export function isPaperNavKey(key: ManageRowKey): key is PaperNavKey {
  return (PAPER_NAV_KEYS as readonly ManageRowKey[]).includes(key);
}

/**
 * The redesign's order for the report rows: the spend-gain report FIRST, as
 * the artboard's "Sales & costs" (2026-10-03). The one row it does not draw
 * (remakes) follows, so the drawn ones sit where the design puts them.
 */
export const PAPER_REPORT_ORDER: readonly ManageRowKey[] = [
  "reports",
  "reportItems",
  "reportStaff",
  "reportDayparts",
  "reportRefunds",
  "shiftHistory",
  "dayClose",
  "reportRemakes",
  "reportSpeed",
  "feedback",
];

/** How long a nav count may be shown before it is read again. */
const NAV_COUNT_STALE_MS = 60_000;

/**
 * The small figures at the end of two PAPER rows, as the redesign draws them:
 * the dishes on the menu beside "Menu", and the ingredients to reorder beside
 * "Stock" (nothing when none are).
 *
 * Each reads the same list, under the same key, its own page reads — so the
 * figure and the page agree, and visiting either warms the other — and only
 * while the session can see that row (the row's
 * presence IS the permission check: the list is filtered upstream).
 *
 * R6 (founder, 2026-10-16 — "count screen only"): on the count screen the
 * Stock figure is neither requested nor read while the open count is BLIND —
 * the ingredient list carries `onHandQty`, and the counter's screen must not
 * fetch it. It waits for the count screen's own read of the open count —
 * one that landed AFTER the screen was entered, never an answer cached on an
 * earlier visit (this hook never fetches that itself) — then asks exactly as
 * before when the count is open-book or none is open. Every other screen is
 * unchanged.
 */
export function usePaperNavCounts(
  items: readonly ManageNavItem[],
): Partial<Record<ManageRowKey, number>> {
  const hasRow = (key: ManageRowKey) =>
    items.some((item) => item.rowKey === key);
  const { pathname } = useLocation();
  const onCountScreen = /\/manage\/stocktake\/?$/.test(pathname);
  // Read from the cache only — the count screen owns this request.
  const openCount = useApiQuery<Stocktake | null>(
    queryKeys.stocktakeCurrent,
    endpoints.restaurant.stocktakeCurrent,
    { enabled: false },
  );
  // The open count as it stood when this screen was entered: an answer
  // cached on an earlier visit (this observer outlives the page) may predate
  // a blind count opened since, so only one fetched after entry counts.
  // (Keyed on entering the count screen, not on the path, so a path change
  // that stays on it — the language — need not wait on a page that may not
  // read again.)
  const [entry, setEntry] = useState(() => ({
    onCountScreen,
    updatedAt: openCount.dataUpdatedAt,
  }));
  if (entry.onCountScreen !== onCountScreen) {
    setEntry({ onCountScreen, updatedAt: openCount.dataUpdatedAt });
  }
  const readSinceEntry =
    entry.onCountScreen === onCountScreen &&
    openCount.dataUpdatedAt > entry.updatedAt;
  const holdStock =
    onCountScreen &&
    (!readSinceEntry ||
      openCount.data === undefined ||
      openCount.data?.blind === true);
  // P7.4: each figure is its own count read, on its list's gate — unless the
  // list itself is on screen (its page, Today), when the figure comes off
  // that read and the count waits. Both wait one deferred pass at mount, so
  // a list opened with the page is seen before a count goes out beside it.
  const menuList = useObservedQueryData<MenuItem[]>(queryKeys.menuItems);
  const stockList = useObservedQueryData<Ingredient[]>(queryKeys.ingredients);
  const wantMenu = useDeferredValue(hasRow("menu") && !menuList.observed, false);
  const wantStock = useDeferredValue(
    hasRow("ingredients") && !holdStock && !stockList.observed,
    false,
  );
  const menu = useApiQuery<MenuOnMenuCount>(
    queryKeys.menuItemsCount,
    endpoints.restaurant.menuItemsCount,
    { enabled: wantMenu, staleTime: NAV_COUNT_STALE_MS },
  );
  const stock = useApiQuery<IngredientReorderCount>(
    queryKeys.ingredientsReorderCount,
    endpoints.restaurant.ingredientsReorderCount,
    { enabled: wantStock, staleTime: NAV_COUNT_STALE_MS },
  );
  const onMenu =
    menuList.observed && menuList.data
      ? menuAvailability(menuList.data).total
      : menu.data?.onMenu;
  const toReorder =
    stockList.observed && stockList.data
      ? ingredientsToReorder(stockList.data).length
      : stock.data?.toReorder;
  const counts: Partial<Record<ManageRowKey, number>> = {};
  if (onMenu !== undefined) counts.menu = onMenu;
  if (toReorder !== undefined && !holdStock) {
    if (toReorder > 0) counts.ingredients = toReorder;
  }
  return counts;
}
